/**
 * Separates a word into alphanumeric characters and punctuation/special characters
 */
export const separateWordParts = (word: string): { chars: string[]; punctuation: string } => {
  // Extract alphanumeric characters
  const alphaNumericChars = word.replace(/[^a-zA-Z0-9]/g, '').split('');
  
  // Extract punctuation and special characters
  const punctuation = word.replace(/[a-zA-Z0-9]/g, '');
  
  return { chars: alphaNumericChars, punctuation };
};

/**
 * Scrambles a text string by randomizing the order of characters in each word
 * while preserving spaces between words and removing punctuation from scrambled version.
 */
export const scrambleText = (inputString: string): string => {
  // 1) Tokenize by space
  const tokens = inputString.split(" ");
  
  // 2) Randomize the order of characters in each token (excluding punctuation)
  const randomizedTokens = tokens.map(token => {
    // Separate alphanumeric characters from punctuation
    const { chars } = separateWordParts(token);
    
    // If no alphanumeric characters, return empty string (will just be punctuation)
    if (chars.length === 0) return '';
    
    // Fisher-Yates shuffle algorithm for the alphanumeric characters only
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    
    // Return only the scrambled alphanumeric characters
    return chars.join('');
  });
  
  // 3) Join with spaces, convert to uppercase, and return
  return randomizedTokens.join(" ").toUpperCase();
};

/**
 * Removes punctuation and special characters from a string
 */
const removePunctuation = (text: string): string => {
  // Remove all non-alphanumeric characters except for spaces
  return text.replace(/[^\w\s]/g, '');
};

/**
 * Checks if a guess matches any of the original words
 * Ignores punctuation, case, and whitespace
 */
export const checkGuess = (
  guess: string, 
  originalWords: string[], 
  solvedIndices: number[]
): number | null => {
  // Normalize the guess by removing punctuation, trimming whitespace, and converting to lowercase
  const normalizedGuess = removePunctuation(guess.toLowerCase().trim());
  
  // Check against each original word that isn't already solved
  for (let i = 0; i < originalWords.length; i++) {
    if (!solvedIndices.includes(i)) {
      // Normalize the original word the same way we normalized the guess
      // This ensures we compare just the alphanumeric characters
      const normalizedOriginal = removePunctuation(originalWords[i].toLowerCase());
      
      if (normalizedOriginal === normalizedGuess) {
        return i;
      }
    }
  }
  
  return null;
};
